import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Chip,
  Avatar,
  Divider,
  alpha,
} from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Public as PublicIcon,
  Storefront as StorefrontIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { StandardizedOnboardingData } from "../utils/onboardingDataTransformer";

interface StoreOverviewProps {
  standardizedData: StandardizedOnboardingData;
  onEdit: () => void;
}

export const StoreOverview: React.FC<StoreOverviewProps> = ({
  standardizedData,
  onEdit,
}) => {
  const { t } = useTranslation();
  const { store, merchant } = standardizedData;

  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Box display="flex" alignItems="center">
            {merchant?.merchantLogo?.src ? (
              <Avatar
                src={merchant.merchantLogo.src}
                sx={{
                  width: 64,
                  height: 64,
                  mr: 3,
                  border: "3px solid",
                  borderColor: alpha("#4caf50", 0.2),
                }}
              />
            ) : (
              <StorefrontIcon sx={{ fontSize: 64, mr: 3, color: "#4caf50" }} />
            )}{" "}
            <Box>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {merchant?.name || t(messages.Store_Name_Default())}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {store?.address || t(messages.Store_Address_Default())}
              </Typography>
              {store?.city && (
                <Typography variant="body2" color="text.secondary">
                  {store.city}, {store.province} {store.zip}
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{
              borderColor: "#4caf50",
              color: "#4caf50",
              "&:hover": {
                borderColor: "#43a047",
                backgroundColor: alpha("#4caf50", 0.05),
              },
            }}
          >
            {t(messages.Edit_Details())}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {" "}
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom color="#4caf50">
              {t(messages.Contact_Information_Overview())}
            </Typography>
            <List dense>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PhoneIcon sx={{ color: "#666" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t(messages.Phone_Label())}
                  secondary={store?.phone || t(messages.Not_provided())}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PublicIcon sx={{ color: "#666" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t(messages.Website_Label())}
                  secondary={
                    merchant?.domain ? (
                      <Button
                        component="a"
                        variant="text"
                        size="small"
                        href={merchant.domain}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ p: 0, justifyContent: "flex-start" }}
                      >
                        {merchant.domain}
                      </Button>
                    ) : (
                      t(messages.Not_provided())
                    )
                  }
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LocationOnIcon sx={{ color: "#666" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t(messages.Coordinates_Label())}
                  secondary={
                    store?.location?.coordinates
                      ? `${store.location.coordinates[1]}, ${store.location.coordinates[0]}`
                      : t(messages.Not_Available())
                  }
                />
              </ListItem>
            </List>
          </Grid>{" "}
          {/* Store Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom color="#4caf50">
              {t(messages.Store_Details_Section())}
            </Typography>
            <List dense>
              <ListItem disablePadding>
                <ListItemText
                  primary={t(messages.Store_Tags_Label())}
                  secondary={
                    store?.storeTag && store.storeTag.length > 0 ? (
                      <Box sx={{ mt: 0.5 }}>
                        {store.storeTag.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag.name}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      t(messages.No_tags_assigned())
                    )
                  }
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemText
                  primary={t(messages.Store_Signature_Label())}
                  secondary={store?.signature || t(messages.Not_specified())}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
